/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { Pointer } from "ol/interaction";
import { Zoom } from "ol/control";
import {
  MapContexts,
  defaultContext,
  MergedProjectResponse,
  currentMap,
} from "./InitialMapValues";
import getMarkerLayer from "../map-layers/getMarkerLayer";
import getProjectHighlightLayer from "../map-layers/getHighlightLayer";
import useMapStates from "./useMapStates";
import { addOrRemoveControlButtons } from "./utils";

const MapContext = createContext<MapContexts>(defaultContext);

export function MapProvider({ children }: { children: React.ReactNode }) {
  const [projects, setProjects] = React.useState<MergedProjectResponse[]>([]);
  const {
    showMap,
    setShowMap,
    showDropdownMenu,
    setShowDropdownMenu,
    showPopup,
    setShowPopup,
    popupInfo,
    setPopupInfo,
    dataId,
    setDataId,
    clientId,
    isMobile,
  } = useMapStates(defaultContext);
  const mapRef = useRef(currentMap);

  const showBiodiversityUnitsForProject = (project: MergedProjectResponse) => {
    const highlightLayer = getProjectHighlightLayer(project);
    if (!highlightLayer) {
      return;
    }
    highlightLayer.setZIndex(1);
    mapRef.current.addLayer(highlightLayer);
  };

  const showBiodiversityUnits = (allProjects: MergedProjectResponse[]) => {
    allProjects.forEach((project) => {
      showBiodiversityUnitsForProject(project);
    });
  };

  useEffect(() => {
    if (mapRef.current) {
      if (isMobile) {
        addOrRemoveControlButtons(mapRef.current, Zoom, "remove");
      } else {
        addOrRemoveControlButtons(mapRef.current, Zoom, "add");
      }
      mapRef.current.updateSize();
      mapRef.current.render();
    }
  }, [clientId, isMobile]);

  useEffect(() => {
    if (mapRef.current && projects.length > 0) {
      const { vectorLayer, selectClick } = getMarkerLayer(
        projects,
        popupInfo,
        setPopupInfo,
        setShowPopup,
      );
      const hoverInteraction = new Pointer({
        handleMoveEvent: (event) => {
          const { pixel } = event;
          const hit = currentMap.hasFeatureAtPixel(pixel);
          const targetElement = currentMap.getTargetElement();
          targetElement.style.cursor = hit ? "pointer" : "default";
        },
      });
      showBiodiversityUnits(projects);
      mapRef.current.addLayer(vectorLayer);
      mapRef.current.addInteraction(hoverInteraction);
      mapRef.current.addInteraction(selectClick);
      const dataIdToSet = "markers";
      vectorLayer.setZIndex(2);
      setDataId(dataIdToSet);
      setShowMap(true);
    } else {
      setShowMap(false);
    }
  }, [
    projects,
    currentMap,
    mapRef,
    setPopupInfo,
    setShowPopup,
    setDataId,
    setShowMap,
  ]);

  const contextValue = useMemo(
    () => ({
      mapRef,
      projects,
      setProjects,
      showMap,
      showDropdownMenu,
      setShowDropdownMenu,
      showPopup,
      setShowPopup,
      popupInfo,
      setPopupInfo,
      isMobile,
      dataId,
    }),
    [
      projects,
      setProjects,
      showMap,
      showDropdownMenu,
      popupInfo,
      showPopup,
      isMobile,
      dataId,
      mapRef,
      setPopupInfo,
      setShowDropdownMenu,
      setShowPopup,
    ],
  );

  return (
    <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>
  );
}

export default MapProvider;

export const useMapContext = () => {
  return useContext(MapContext);
};
